/**
 * Term sheets — a structured, per-`marketUid` description of every lend and
 * borrow offer we serve.
 *
 * One shape for pool lenders, fixed-term lenders, CDPs and vaults, so an
 * integrator reads ONE object instead of ~15 protocol-specific fields plus a
 * per-lender mental model. See [TERM_SHEET_PLAN.md](../../../../TERM_SHEET_PLAN.md)
 * for the design rationale and the per-lender coverage matrix.
 *
 * ## Conventions that hold everywhere in this file
 *
 * - **Rates are nominal APR in PERCENT** (`3.85` = 3.85 %/yr), never a
 *   fraction and never an APY. This is the package-wide convention.
 * - **Factors are fractions** (`0.85` = 85 % LTV) — matching `LenderConfigData`.
 * - **Durations are SECONDS**, timestamps are **unix seconds**.
 * - **Raw amounts are decimal strings** in base units; human amounts are
 *   `number`.
 * - Every string union is deliberately OPEN (`| (string & {})`) so a new
 *   lender can introduce a member without breaking a consumer's exhaustive
 *   switch. Consumers MUST have a `default` branch and fall back to
 *   `info.headline`.
 */

import type { VaultExitRoute } from '../vaults/exitRoutes'

/** Nominal APR in percent (`3.85` = 3.85 %/yr). Never a fraction, never APY. */
export type AprPercent = number

/**
 * Open-enum helper. `Open<'a' | 'b'>` keeps autocomplete for the known members
 * while still accepting any string, so adding a member later is an ADDITIVE
 * change rather than a breaking one.
 */
export type Open<T extends string> = T | (string & {})

// ---------------------------------------------------------------------------
// Shared primitives
// ---------------------------------------------------------------------------

/** Compact token reference — enough to render without a second lookup. */
export interface TermAssetRef {
  chainId: string
  /** Lowercased contract address. */
  address: string
  symbol?: string
  name?: string
  decimals?: number
  assetGroup?: string
  logoURI?: string
}

/**
 * Machine tags for filtering/faceting. DERIVED from the structured fields in
 * `tags.ts` — never hand-written per lender, so they cannot drift from the
 * numbers they summarize.
 */
export type TermTag = Open<
  | 'fixed-rate'
  | 'variable-rate'
  | 'user-set-rate'
  | 'zero-interest'
  | 'prepaid-interest'
  | 'nav-accrual'
  | 'has-maturity'
  | 'perpetual'
  | 'rolling-duration'
  | 'static-debt'
  | 'accruing-debt'
  | 'time-liquidation'
  | 'price-liquidation'
  | 'redeemable'
  | 'no-liquidation'
  | 'full-collateral-seizure'
  | 'early-exit-free'
  | 'early-exit-penalty'
  | 'early-exit-discount'
  | 'exit-instant'
  | 'exit-capped'
  | 'exit-cooldown'
  /**
   * The DEBT cannot be repaid for a period after opening or topping up.
   *
   * Deliberately distinct from `exit-cooldown` (a supply-side withdrawal
   * delay): this one means the position cannot be closed, deleveraged or
   * migrated out — and, on the lender that has it, that an incoming
   * liquidation cannot be averted by repaying.
   */
  | 'repay-locked'
  | 'exit-queued'
  | 'exit-market-sale'
  | 'exit-may-be-impossible'
  | 'permissioned'
  | 'capped'
  | 'cap-full'
  | 'first-loss'
  | 'socialized-loss'
  | 'physical-delivery'
  | 'undercollateralized'
  | 'nav-attested'
  | 'immutable'
  | 'no-timelock'
  | 'eoa-controlled'
  | 'points-rewards'
  /**
   * The headline yield is MOSTLY the asset's own (staking / RWA / savings)
   * yield, not interest this market pays. Set at >= 50 % of `aprTotal`.
   *
   * Worth a tag rather than only a detail row, because the two are not
   * interchangeable and the difference is invisible in a single percentage: a
   * market rate is paid by borrowers and moves with utilization, while
   * intrinsic yield you would earn holding the asset in your wallet — you are
   * taking this market's risk for the DIFFERENCE, not for the headline.
   * Decisive when looping: a loop only carries if the collateral out-earns the
   * debt, and intrinsic yield does not scale with leverage the way a lending
   * spread does.
   */
  | 'intrinsic-yield'
  /**
   * This market pays NO interest of its own — the entire headline is intrinsic
   * yield and/or rewards. Curvance's collateral-only legs are the canonical
   * case (`debtCap == 0` ⇒ 0 % supply APR by construction), but any market
   * with no borrowers reads the same way.
   */
  | 'no-market-interest'
  | 'oracle-flagged'
  | 'no-oracle'
>

/** Human-facing copy for one side of a term sheet. */
export interface TermInfo {
  /**
   * ≤ ~100 chars, templated from live numbers, ready to render.
   * `"Fixed 4.12 % until 3 Sep 2026 · repay any time at face value"`.
   *
   * ALWAYS populated — it is the graceful-degradation path for a consumer
   * that does not recognise a newer enum member.
   */
  headline: string
  /** 1–3 sentences. Invariant prose lives on the profile; this interpolates
   *  the market's own values. */
  description: string
  /** Ready-to-display consequences, most important first. */
  implications?: string[]
  tags: TermTag[]
}

// ---------------------------------------------------------------------------
// Rate
// ---------------------------------------------------------------------------

export type RateKind = Open<
  /** Utilization IRM (Aave, Compound, Morpho, Silo, Euler, Fluid…). */
  | 'variable-curve'
  /** Governance-set with no curve (USDD stability fee, Spark `vsr`). */
  | 'variable-managed'
  /** Borrower picks the rate (Liquity family). */
  | 'user-set'
  /** Locked for a maturity (Exactly, Midnight, Term, TermMax, Teller, Lista). */
  | 'fixed-term'
  /** Locked, no maturity. Reserved — nothing uses it today. */
  | 'fixed-open'
  /** No ongoing rate at all (River); cost is a one-off fee. */
  | 'zero-interest'
  /** Interest prepaid in a separate token (Inverse DBR). */
  | 'prepaid'
  /** Share price tracks an attested NAV (Re, Apyx, USPC). */
  | 'nav-accrual'
  /**
   * There is no rate MECHANISM at all — the number is the realized result of a
   * trading or market-making book over a past window (GMX GM/GLV, HyperCore
   * vaults).
   *
   * Deliberately distinct from both `variable-*` (which implies something sets
   * the rate, and that it is a rate) and `none` (which means no yield). A
   * realized return is not a promise about the future in any degree, and the
   * principal that produced it can fall — see `principal.risks`.
   */
  | 'realized'
  /** Pure collateral leg — no yield. */
  | 'none'
>

/** One reward program. Identity matters: points are not a bankable APR. */
export interface RewardTerm {
  /** Absent for points programs — that absence IS the signal. */
  asset?: TermAssetRef
  kind: Open<'token' | 'points' | 'unknown'>
  apr: AprPercent
  side: 'supply' | 'borrow'
  /** How it is realized — decides whether the APR is actually bankable. */
  claim: Open<'accrual' | 'merkl' | 'manual' | 'none'>
  /** Program end, where known. An APR with two weeks left is not an APR. */
  endsAt?: number
  /**
   * `true` ⇒ not priceable (points). MUST be shown separately and is
   * deliberately EXCLUDED from `RateTerms.aprTotal`.
   */
  indicative?: boolean
}

/** One entry in a fixed-term rate menu. Mirrors `MarketTermEntry`. */
export interface RateMenuEntry {
  /** LENDER-SPECIFIC: Exactly/TermMax = unix maturity, Teller = duration
   *  seconds, Lista = broker product id, Midnight/Term = `0`. */
  termId: number
  durationSecs: number
  durationDays: number
  /** Borrow APR at this term. */
  apr: AprPercent
  /** Lend APR at this term, where the lender quotes both sides. */
  depositApr?: AprPercent
  /** Borrowable liquidity at this term, human units. */
  available?: number
}

export interface RateTerms {
  kind: RateKind
  /** Base rate only — no rewards, no intrinsic yield. */
  apr: AprPercent
  components: {
    base: AprPercent
    /** Priceable rewards only. */
    rewards?: AprPercent
    /** Underlying/LST yield the asset earns by itself. */
    intrinsic?: AprPercent
  }
  /** `base + priceable rewards + intrinsic` — the headline number. */
  aprTotal: AprPercent
  /** Carried explicitly even though it has one value today: a silent
   *  APR→APY change would be the classic undetectable break. */
  basis: 'apr-nominal'
  /**
   * WHAT PERIOD the number describes — orthogonal to {@link basis} (the unit)
   * and to `source` (where it came from).
   *
   * Lenders never need this: an IRM rate is the rate right now. Vaults do, and
   * the difference is not cosmetic — a vault reporting a 30-day TRAILING 14 %
   * and a market quoting a forward 14 % are different claims, and a leaderboard
   * that sorts them in one column silently ranks past performance against a
   * live offer. Every vault provider that measures rather than quotes carries
   * the window in its own dialect (`YearnVault.isForwardApr`,
   * `LagoonVault.aprWindow`, HyperCore's `apr`, any realized share-price APR);
   * this is the one field they normalize onto.
   *
   * Absent ⇒ `spot`, i.e. the ordinary "rate right now" reading.
   */
  window?: {
    /**
     *  - `spot` — the rate in force this block (an IRM, a governance-set rate).
     *  - `forward` — a projection of what the position WILL earn (a PT's
     *    implied APY, Yearn's forward APR).
     *  - `trailing` — MEASURED over a past window and therefore not a promise.
     */
    kind: Open<'spot' | 'forward' | 'trailing'>
    /** Length of the measurement window, seconds. `trailing` only. */
    secs?: number
    /** The source's own label when it does not map to a duration
     *  (`'inception'`, `'weekly'`). Rendered verbatim. */
    label?: string
  }
  compounding: Open<'per-second' | 'per-block' | 'none' | 'unknown'>
  /**
   * Seconds a FRESH deposit earns NOTHING before the rate starts applying.
   *
   * A warm-up, and it is a different fact from every other delay on a sheet.
   * `SupplyExitTerms.cooldownSecs` is how long your money is STUCK;
   * `GovernanceTerms.timelockSecs` is how long you have to react to someone
   * changing the deal. This is neither: the money is free to leave at any
   * moment, and the deal is not changing — you simply do not earn yet. Putting
   * it in either of the other two would describe a lock that does not exist.
   *
   * The reason it needs a field rather than a sentence: with it absent, a
   * headline reading "Variable 3.5 % · withdraw any time" is composed of two
   * true halves that together mislead, because a stay shorter than the warm-up
   * realises exactly ZERO. Frankencoin's savings module is the case that forced
   * it (`INTEREST_DELAY` = 3 days, and a top-up re-weights the whole position's
   * clock pro-rata rather than only the new money).
   *
   * Absent ⇒ the rate applies from the first block, which is the norm.
   */
  warmupSecs?: number
  source: Open<
    | 'utilization-curve'
    | 'orderbook'
    | 'auction'
    | 'governance'
    | 'borrower'
    | 'oracle'
    | 'api'
    | 'derived'
  >
  /** Is the rate locked for the life of the position? */
  isLocked: boolean
  /** Protocol-enforced bounds (Liquity min/max, Morpho rateCap/rateFloor). */
  minApr?: AprPercent
  maxApr?: AprPercent
  /**
   * `kind: 'user-set'` only — the contract a RATE SETTER needs.
   *
   * On the Liquity family the borrower picks their own rate at open, and the
   * choice is not cosmetic: it decides where you sit in the redemption queue,
   * so the cheapest rate is also the one that gets redeemed first. A borrow UI
   * that offers no input either cannot build the transaction or silently
   * accepts a default the user never saw.
   *
   * `default` is what the protocol applies when the caller omits a rate (the
   * branch average), so a UI can pre-fill it and stay consistent with what the
   * action would have done anyway.
   */
  userSet?: {
    /** Is a rate part of the open call at all? */
    required: boolean
    /** Inclusive bounds, PERCENT. Outside these the open reverts. */
    min?: AprPercent
    max?: AprPercent
    /** Applied when the caller omits one — the branch average. */
    default?: AprPercent
    /** Can it be changed after opening (Liquity `set-rate`)? */
    adjustable: boolean
    /**
     * Changing the rate re-charges the upfront fee — so it is not free, and a
     * UI that presents it as a slider should say so.
     */
    adjustmentCostNote?: string
    /** Seconds before the rate may be adjusted again without penalty. */
    adjustmentCooldownSecs?: number
  }
  /** Per-program reward detail — token identity, claim path, end date. */
  rewards?: RewardTerm[]
  /** Rate menu when the market offers several terms at once. */
  menu?: RateMenuEntry[]
  /** Size the quote is valid for, when the rate is depth-dependent. */
  quote?: { assets: number; basis: Open<'marginal' | 'average'> }
  lastChangedAt?: number
}

// ---------------------------------------------------------------------------
// Maturity
// ---------------------------------------------------------------------------

export interface MaturityTerms {
  kind: Open<'perpetual' | 'fixed-date' | 'rolling-duration'>
  /** unix seconds; `kind: 'fixed-date'`. */
  maturity?: number
  /** ISO-8601 mirror so consumers need not re-format. */
  maturityIso?: string
  /** Snapshot — derive live from `maturity` for a countdown. */
  secondsToMaturity?: number
  /** `kind: 'rolling-duration'` (Teller, Lista broker). */
  minDurationSecs?: number
  maxDurationSecs?: number
  /**
   * What happens at/after maturity if NOBODY acts. The field that most
   * surprises users — outcomes range from "interest simply stops" to
   * "liquidated within five minutes, losing all collateral".
   */
  atMaturity?: Open<
    | 'stops-earning'
    | 'penalty-accrues'
    | 'liquidatable'
    | 'default-seizure'
    | 'physical-delivery'
    | 'refinanced'
    | 'auto-roll'
    | 'none'
  >
  /** Grace window before `atMaturity` bites (Teller ~300 s, TermMax 7200 s). */
  graceSecs?: number
}

// ---------------------------------------------------------------------------
// Fees
// ---------------------------------------------------------------------------

export type FeeWhen = Open<
  'entry' | 'ongoing' | 'exit' | 'late' | 'liquidation' | 'performance'
>

/**
 * One charge, in a shape general enough that a NEW fee is data rather than a
 * schema change. Replaces the current scatter: `originationFee`,
 * `withdrawFeeBps`, `rates.fee`, `fixedTerm.fees.*`, `river.mintFeeRate`,
 * `liquity.gasCompensation`.
 */
export interface FeeTerm {
  /** Stable slug — the join key for UI copy and filtering. */
  id: Open<
    | 'origination'
    | 'late-penalty'
    | 'early-repay-penalty'
    | 'early-repay-discount'
    | 'continuous'
    | 'settlement'
    | 'instant-exit'
    | 'performance'
    /** Charged on ASSETS and ongoing, not on yield — so it is owed in a flat
     *  year, which a performance fee is not. */
    | 'management'
    | 'reserve-factor'
    | 'gas-compensation'
    | 'redemption'
    | 'claim'
    | 'liquidation-bonus'
  >
  /** Human label — lets an unknown `id` still render correctly. */
  label: string
  when: FeeWhen
  unit: Open<'apr-percent' | 'percent' | 'bps' | 'absolute'>
  basis: Open<
    | 'principal'
    | 'face-value'
    | 'yield'
    | 'collateral'
    | 'shares'
    | 'debt-repaid'
  >
  /**
   * A NEGATIVE value is legal and means a REBATE (Exactly's early-repay
   * discount). Sign is load-bearing — never take an absolute value.
   */
  value: number
  payee?: Open<'protocol' | 'lenders' | 'liquidator' | 'curator' | 'gas-refund'>
  /** Governance-mutable ⇒ this is a snapshot; re-verify before quoting. */
  mutable?: boolean
  /**
   * Protocol-enforced CEILING on `value`, same `unit`. Only meaningful
   * alongside `mutable: true`, and it is what makes that flag actionable: a
   * mutable fee with no stated bound reads as unlimited discretion, when the
   * contract may in fact refuse anything above a hard constant.
   *
   * Morpho Blue is the case — its market parameters cannot be changed at all,
   * but the owner may set a fee on interest up to a `MAX_FEE` of 25 %. "The fee
   * can change" and "the fee can change, but never above 25 %, and nothing else
   * about this market can change" are very different sentences, and only the
   * second one is true.
   *
   * Absent ⇒ no cap is known. NOT the same as "uncapped".
   */
  cap?: number
  /** Only resolvable at action time (Exactly discount, TermMax curve price). */
  indicative?: boolean
  /** Decaying/scheduled fees (Apyx: 3.40 % → 0 over 20 days). */
  schedule?: { afterSecs: number; value: number }[]
  description?: string
}

// ---------------------------------------------------------------------------
// Exit
// ---------------------------------------------------------------------------

/** Superset of `SavingsWithdrawalMode` + `LstWithdrawalMode`, plus the two
 *  lending-side exits neither covers. */
export type SupplyExitMode = Open<
  | 'instant'
  | 'instant-capped'
  | 'instant-or-queued'
  | 'fee-or-queued'
  | 'fixed-cooldown'
  | 'queued'
  | 'request-based'
  /** Sell the instrument on a book (Term, TermMax, Midnight). */
  | 'market-sale'
  /** No early exit at all. */
  | 'at-maturity'
  | 'off-chain'
  | 'dex-only'
>

/**
 * One concrete way out, as published by the vault row. Re-exported from the
 * vault layer so a term sheet and the `/v1/data/vaults` row cannot disagree
 * about what the legs are.
 */
export type SupplyExitRoute = VaultExitRoute

export interface SupplyExitTerms {
  mode: SupplyExitMode
  /**
   * **Every leg of the exit, enumerated.**
   *
   * `mode` names the shape and the rest of this block describes it with ONE
   * number each — one `cooldownSecs`, one liquidity reading, one fee list.
   * That is a lie for the two-legged modes: `fee-or-queued` charges the fee on
   * the instant leg and applies the wait to the free one, so a reader taking
   * `cooldownSecs` and the exit fee together concludes it must pay 0.5 % AND
   * wait a week, which is true of no leg. `instant-or-queued` has the same
   * problem in the other direction.
   *
   * The routes are the per-leg truth: fee, wait, minimum size and capacity,
   * each attributed to the leg it actually belongs to. Ordered cheapest-first
   * where the legs are equally reachable — but read `minAmount`, because a
   * cheap leg with a floor above a holder's position is not an option they
   * have (Treehouse's 5 bps queue starts at 50 wstETH).
   *
   * Always at least one entry for a vault row; absent on lending markets,
   * whose exit is the pool's liquidity rather than a set of routes.
   */
  routes?: SupplyExitRoute[]
  /**
   * Coarse alias — identical semantics to
   * `VaultClassificationFields.redemptionType`, plus one member that field
   * cannot express.
   *
   * `'market'` ⇒ the instrument has NO redemption model at all: you leave by
   * selling it (a Pendle PT). Both `sync` and `async` are wrong there and both
   * mislead — `sync` invites the stuck-capital heuristic against what is really
   * pool depth, `async` implies a queue that does not exist — which is why
   * `classifyRedemption` returns `undefined` rather than picking one. The term
   * sheet answers positively instead of leaving a hole.
   */
  settlement: Open<'sync' | 'async' | 'market'>
  cooldownSecs?: number
  /** Claim-window constraints (Apyx: blocked 3 d, free at 20 d). */
  claimWindow?: { earliestSecs?: number; freeAfterSecs?: number }
  /** What can actually leave right now. */
  liquidity?: { assets: number; assetsUsd?: number; ratio?: number }
  partialAllowed: boolean
  /**
   * Does exiting early cost an UNKNOWN amount?
   * `none` par · `haircut-formula` deterministic discount ·
   * `market-price` you sell into a book · `may-be-impossible` the book can be
   * empty.
   */
  priceRisk: Open<
    'none' | 'haircut-formula' | 'market-price' | 'may-be-impossible'
  >
  cancellable?: boolean
  /** The `when: 'exit' | 'performance'` subset of the side's fees. */
  fees: FeeTerm[]
}

export interface BorrowExitTerms {
  /** Three signs exist across our lenders: `discount` is a REBATE (Exactly). */
  earlyRepay: Open<
    'free' | 'discount' | 'penalty' | 'market-price' | 'not-allowed'
  >
  atMaturityCost: Open<'face' | 'accrued'>
  lateBehaviour: Open<
    | 'penalty-accrues'
    | 'liquidatable'
    | 'default-seizure'
    | 'refinanced'
    | 'none'
  >
  partialAllowed: boolean
  /** Dust floor — Liquity `minDebt`, Morpho/Lista `minLoan`. Raw base units. */
  minDebt?: string
  /** Over-repay REVERTS (Midnight `uint128` underflow) — a real footgun. */
  overRepayReverts?: boolean
  /**
   * Seconds after OPENING (or topping up) during which the debt cannot be
   * repaid at all.
   *
   * The mirror of `SupplyExitTerms.cooldownSecs`, and deliberately a separate
   * field because it is a categorically worse term: a supply cooldown delays
   * your money, a REPAY cooldown means a position cannot be closed,
   * deleveraged or migrated out — and, on the one lender that has it, means an
   * incoming liquidation cannot be averted by repaying either.
   *
   * Only Curvance (`MIN_HOLD_PERIOD`, 1200 s) has one today, which is exactly
   * why it needs to be stated rather than assumed away: every other lender we
   * carry lets you exit at any block, and the generic builder's
   * `availability.canClose: true` encodes that assumption.
   */
  cooldownSecs?: number
  fees: FeeTerm[]
}

// ---------------------------------------------------------------------------
// Liquidation
// ---------------------------------------------------------------------------

/** One named penalty, for models where a single number is not enough. */
export interface LiquidationPenaltyTerm {
  /** Stable slug: 'stability-pool' | 'redistribution' | 'hard' | 'protocol-fee'. */
  id: Open<string>
  label: string
  /** Fraction of the repaid debt (0.05 = 5 %). */
  value: number
  description?: string
}

/**
 * Redemption — collateral taken from a HEALTHY position.
 *
 * This is the term users most often misread, because the effect ("your
 * collateral can be taken") sounds like a governance power or a liquidation
 * when on every lender we serve it is neither: it is a permissionless
 * arbitrage that defends the stablecoin's peg.
 *
 * **The per-lender answers are audited in CDP_REDEMPTION_TERMS.md.** Read it
 * before filling this block for a new CDP: the fields below vary INDEPENDENTLY
 * across protocols, and copying Liquity V2's answers — the best-documented CDP,
 * and therefore the one that gets copied — has already produced three wrong
 * sheets. Spelling out WHO can trigger
 * it, WHEN it pays them to, WHICH positions are hit and WHAT the borrower can
 * do about it turns an alarming sentence into an actionable one.
 */
export interface RedemptionTerms {
  /**
   * WHO can trigger it — a statement about PERMISSION, not about frequency.
   *
   *  - `permissionless-arbitrage` — any holder of the debt token can call it,
   *    without a vote and without targeting anyone personally.
   *  - `governance` / `protocol` — reserved; nothing uses these today.
   *
   * Read together with {@link driver}, which says when it actually PAYS. The two
   * are independent and conflating them overstates the risk: on every lender we
   * serve the call is open at any block, but under `below-peg` it is only
   * profitable while the stablecoin trades under target, so redemptions arrive
   * in bursts during depegs rather than continuously.
   */
  trigger: Open<'permissionless-arbitrage' | 'governance' | 'protocol'>
  /**
   * WHEN it becomes economically rational. `below-peg` means redemptions only
   * pay when the stablecoin trades under its target, which is exactly why they
   * exist — they push it back up.
   */
  driver?: Open<'below-peg' | 'always'>
  /**
   * WHICH positions are hit first.
   *  - `lowest-rate-first` — Liquity V2 family: the cheapest borrowers are
   *    redeemed first, so the rate you chose IS your queue position.
   *  - `pro-rata` — Resupply: skimmed from EVERY borrower in the pair, so
   *    there is no queue and nothing to out-run.
   *  - `lowest-collateral-ratio` — Liquity V1 lineage.
   */
  order: Open<'lowest-rate-first' | 'pro-rata' | 'lowest-collateral-ratio'>
  /**
   * Does the borrower end up down in USD terms? On Liquity V2 in NORMAL
   * operation the redemption fee stays IN the trove as extra collateral, so the
   * borrower is roughly USD-neutral — what they lose is COLLATERAL EXPOSURE,
   * not value. Saying "you can lose your collateral" without this overstates it.
   *
   * **Do not copy that answer to another protocol without checking where the
   * fee goes.** It is a property of Liquity's specific mechanism, not of
   * redemptions generally: Resupply writes the collateral off across the pair
   * with half the fee going to the protocol and nothing credited back, and a
   * shut-down Liquity branch pays the redeemer a 2 % bonus out of the
   * borrower's collateral. ABSENT means we have not established it, which is
   * the honest state for anything but the two cases above.
   */
  valueImpact?: Open<'usd-neutral' | 'loss'>
  /** What the borrower can actually do. Absent ⇒ nothing. */
  defence?: string
}

export interface LiquidationTerms {
  /**
   * HOW liquidation happens mechanically — distinct from WHAT triggers it.
   *
   * Without this, every market reads as the ordinary "a liquidator repays your
   * debt and seizes collateral plus a bonus" model, which is wrong for four of
   * the lenders we serve and materially misleading for one:
   *
   *  - `soft-band`      Collateral is converted GRADUALLY inside the market's
   *                     own AMM as the price enters a band range — with NO
   *                     penalty and REVERSIBLY (Curve LlamaLend). There is no
   *                     single liquidation price at all.
   *  - `stability-pool` A pool absorbs the debt first and only falls back to
   *                     redistributing it across other borrowers (Liquity,
   *                     River) — the two paths carry DIFFERENT penalties.
   *  - `auction`        Price is discovered by a Dutch auction rather than an
   *                     oracle (Frankencoin challenges).
   *  - `default-seizure` The whole escrow is forfeit on a missed payment
   *                     (Teller).
   *  - `delivery`       Unpaid collateral is delivered to lenders (TermMax).
   *  - `repay-to-target-hf`
   *                     There is no close factor at all: the liquidator repays
   *                     however much it takes to restore the position to
   *                     {@link targetHealthFactor}, and the bonus SCALES with
   *                     how far under water it is (Aave V4). See
   *                     {@link healthFactorForMaxBonus}.
   */
  model?: Open<
    | 'repay-seize'
    | 'soft-band'
    | 'stability-pool'
    | 'auction'
    | 'default-seizure'
    | 'delivery'
    | 'repay-to-target-hf'
    | 'none'
  >
  /** Who ends up holding the seized collateral. */
  absorber?: Open<
    'liquidator' | 'stability-pool' | 'other-borrowers' | 'lenders' | 'amm'
  >
  /**
   * Can the position come BACK out of liquidation if the price recovers?
   * True only for `soft-band`: conversion is continuous and reverses, so
   * "being liquidated" is not terminal the way it is everywhere else.
   */
  reversible?: boolean
  /**
   * Named penalties when `penalty` alone cannot express the model — Liquity
   * charges a different rate depending on whether the Stability Pool absorbs
   * the debt or it is redistributed. `penalty` stays the headline (worst or
   * primary) so a naive consumer is still correct.
   */
  penalties?: LiquidationPenaltyTerm[]
  /** Window between becoming liquidatable and the terminal outcome (TermMax). */
  windowSecs?: number
  /** Only allowlisted keepers may liquidate. Absent/false ⇒ permissionless. */
  permissioned?: boolean
  /** Where a shortfall goes when the collateral does not cover the debt. */
  badDebt?: Open<
    | 'socialized'
    | 'redistributed'
    | 'insurance-fund'
    | 'protocol-absorbed'
    | 'unknown'
  >
  /**
   * `soft-band` only: the collateral factor is a FUNCTION of the band count
   * chosen at open (`{ [N]: ltv }` — 0.991 at N=4 vs 0.886 at N=50). `ltv`
   * reports the market's default N; a consumer quoting a different N must read
   * this curve instead.
   */
  bandLtv?: Record<string, number>
  /** Band count `ltv` / `liquidationLtv` were computed at. */
  defaultBands?: number
  /**
   * The knob the BORROWER turns at open, when the factors above depend on one.
   *
   * Mirrors `ConfigEntry.openParameter` — it is the same descriptor, surfaced on
   * the term sheet because that is where a UI edits terms rather than reads
   * them. Describes the DOMAIN only; the value a given position chose lives in
   * the per-position `modes[posId]` slot.
   *
   * Prefer this over {@link bandLtv} to drive a CONTROL: the domain is always
   * known (`MIN_TICKS`/`MAX_TICKS`), whereas the curve is absent on any market
   * whose geometry could not be read. Where the curve IS present it now covers
   * the whole domain, so `bandLtv[N]` is the right source for the LTV that a
   * chosen `N` implies. See POSITION_PARAMETERS_PLAN.md.
   */
  openParameter?: {
    kind: 'llamalend-bands' | 'interest-rate'
    dimension: 'collateralFactor' | 'rate'
    domain: { min: number; max: number } | { values: number[] }
    default: number
    immutableAfterOpen: boolean
    adjustCooldownSeconds?: number
  }
  /**
   * Aave-style escalation: the close factor rises to 1 once health falls below
   * this. Without it, `closeFactor: 0.5` understates the worst case.
   */
  /**
   * The health factor BELOW WHICH the position becomes liquidatable, when the
   * protocol's trigger is not the usual `HF < 1`.
   *
   * Added for Flying Tulip, whose `marginHfTargetBps = 12500` makes a position
   * liquidatable at **HF < 1.25** — `liquidateFlash` reverts above it. Every
   * consumer in this repo otherwise assumes `HF < 1 ⇒ liquidatable`, which
   * understates the danger zone by a quarter of a point of health on the side
   * that costs the borrower money.
   *
   * ABSENT means the ordinary `HF < 1`. Do not default it to 1 at the call
   * site — an explicit 1 and an absent field should read the same, and a
   * lender that has no health factor at all (Frankencoin's challenge game,
   * Teller's time-based default) must not acquire one by omission.
   */
  liquidationHealthFactor?: number

  fullCloseBelowHealthFactor?: number
  /**
   * `repay-to-target-hf` only: the health factor at or below which the
   * liquidator's bonus reaches its maximum — i.e. the value {@link penalty}
   * actually describes.
   *
   * Load-bearing next to a scaling bonus, because the two fields say different
   * things: Aave V4's bonus grows from ~0 at HF 1 to `maxLiquidationBonus`
   * here, so publishing the max alone reads as a flat penalty every liquidation
   * charges, and publishing nothing reads as a market with no penalty at all.
   */
  healthFactorForMaxBonus?: number
  trigger: Open<'price' | 'time' | 'price-and-time' | 'redemption' | 'none'>
  /** Max LTV at open. */
  ltv?: number
  /** Threshold at which liquidation becomes possible. */
  liquidationLtv?: number
  /**
   * Fraction of repaid debt paid to the liquidator on top of par.
   *
   * OPTIONAL, and the distinction is load-bearing: `0` means "the liquidator
   * gets no bonus", `undefined` means "we do not know it". The first cut
   * defaulted the unknown case to `0` and every market on `/lending/latest`
   * rendered a confident "Liquidator takes debt repaid + 0%" — the origin's
   * `market_config` had no penalty column at all, so NOTHING was known. A row
   * we cannot fill must be absent, not zero.
   */
  penalty?: number
  closeFactor: number
  targetHealthFactor?: number
  /**
   * `full-collateral` is the Teller case: the liquidator takes the ENTIRE
   * escrow, not a proportional slice — ~2× the debt at 50 % LTV.
   */
  seizure: Open<'proportional' | 'full-collateral'>
  /**
   * Collateral can be taken while the position is perfectly healthy.
   * See {@link RedemptionTerms} for what actually triggers it — the bare flag
   * says only THAT it can happen, never why, and "your collateral can be
   * taken" reads as a governance power or a bug unless the mechanism is
   * spelled out.
   */
  redeemable?: boolean
  /** How redemption works, when `redeemable`. */
  redemption?: RedemptionTerms
  gracePeriodSecs?: number
}

// ---------------------------------------------------------------------------
// Counterparty / availability / constraints
// ---------------------------------------------------------------------------

export interface CounterpartyTerms {
  kind: Open<
    | 'pool'
    | 'orderbook'
    | 'auction'
    | 'broker'
    | 'cdp'
    | 'p2p'
    | 'vault-strategy'
    | 'off-chain-credit'
  >
  address?: string
  /** The trust question, one field. */
  solvency: Open<
    | 'overcollateralized'
    | 'tranched-senior'
    | 'tranched-junior'
    | 'undercollateralized'
    | 'nav-attested'
  >
  socializedLoss?: boolean
  curator?: string
}

/** Origination window for auction-gated markets (Term Finance). */
export interface AuctionWindow {
  status: Open<'upcoming' | 'open' | 'revealing' | 'closed'>
  canBorrow: boolean
  canLend: boolean
  secondsUntilClose?: number
  id?: string
  startTime?: number
  revealTime?: number
  endTime?: number
  minBorrowAmount?: string
  minLendAmount?: string
}

/** What must be granted BEFORE an action can even be built. */
export type PermissionKind = Open<
  | 'token-approval'
  | 'lender-delegation'
  | 'manager-authorization'
  | 'eip712-permit'
  | 'nft-approval'
  | 'operator-set'
  /** Contract callers must be governance-approved (Inverse, Fraxlend). */
  | 'caller-allowlist'
>

export interface AvailabilityTerms {
  /** Gate CTAs on THIS and nothing else — it already folds in caps, freezes,
   *  auction windows and gating. */
  canOpen: boolean
  canClose: boolean
  /** Machine-readable reason when `canOpen` is false. */
  blockedBy?: Open<
    | 'frozen'
    | 'paused'
    | 'cap-full'
    | 'auction-closed'
    | 'no-liquidity'
    | 'not-whitelisted'
    | 'shutdown'
    | 'disabled'
  >
  gating: Open<
    | 'permissionless'
    | 'whitelist'
    | 'attestation'
    | 'kyc'
    | 'allowlist-contract'
  >
  /** Absent ⇒ no window applies. NOT the same as `closed`. */
  window?: AuctionWindow
  /**
   * Minimum position size to OPEN, in RAW base units of THIS side's asset.
   *
   * On the borrow side this is a minimum DEBT (Comet `baseBorrowMin`, Liquity
   * `minDebt`, Maker-fork `dust`). On the supply/collateral side it is a
   * minimum COLLATERAL — a genuinely different gate, and the only one some
   * lenders have: Frankencoin caps nothing on the debt but refuses a position
   * below `minimumCollateral`, and dropping under it closes the position
   * permanently.
   */
  minSize?: string
  cap?: string
  /** 0..1 — how full the cap is. */
  capUtilization?: number
  requires?: PermissionKind[]
  /**
   * Can a position be OPENED from this side alone, or only by supplying both
   * legs in one action?
   *
   * Absent / `'standalone'` ⇒ the ordinary pool case: a deposit on its own
   * creates a supply position, a borrow on its own draws against whatever
   * collateral the account already has.
   *
   * `'both-legs'` ⇒ the position does not exist until collateral AND debt are
   * committed together, so neither side can open it. This is the CDP shape and
   * it is enforced differently by each protocol — Liquity and its forks make it
   * structurally impossible (`openTrove` refuses anything under `minDebt`);
   * Frankencoin technically ACCEPTS `clone(…, initialMint = 0, …)` but the
   * result is a position that holds collateral, earns nothing and has no debt,
   * which is not a product. Either way the CTA belongs on the joint action
   * (`/v1/actions/lending/deposit-and-borrow`), not on this side.
   *
   * **This is about OPENING only.** Topping up or drawing further against an
   * EXISTING position is a normal standalone op on every one of these lenders,
   * which is exactly why `depositsEnabled` cannot carry this meaning — setting
   * it false would also block the top-up the protocol permits.
   *
   * The flag is genuinely discriminating, not a family label: Resupply is a CDP
   * too, and its `addCollateral(amount, borrower)` opens an unlevered
   * collateral-only position perfectly well, so it stays `'standalone'`.
   */
  opensWith?: Open<'standalone' | 'both-legs'>
}

export interface PositionConstraints {
  /** Aave isolation mode: capped debt, no collateral mixing. */
  isolation?: {
    enabled: boolean
    debtCeiling?: string
    ceilingUtilization?: number
  }
  /** Borrowing this asset forbids borrowing any other in the same account. */
  siloedBorrowing?: boolean
  crossMargin: boolean
  /**
   * How a position is ADDRESSED. `loanId` already means five different things
   * across our lenders and `termId` six — making the model explicit is
   * cheaper than making every integrator rediscover it.
   */
  positionModel: Open<
    'account' | 'sub-account' | 'nft' | 'cdp-id' | 'loan-id' | 'escrow'
  >
  /** One line saying what the id in `loanId`/`posId` actually IS here. */
  positionIdMeaning?: string
  maxPositions?: number
  /**
   * What this fetch actually SAW, as opposed to what the lender family
   * implies. Kept separate from `crossMargin` / `positionModel` on purpose:
   * those are family-level truths from the registry, these are per-fetch
   * observations, and collapsing the two would let a thin chain (a lender
   * listing one asset today) masquerade as a structural property.
   *
   * Useful precisely where they DISAGREE — e.g. Fluid is registered isolated
   * because its T1 vaults dominate, but its T2–T4 "smart" vaults genuinely
   * pool two collaterals; a `collateralAssetCount > 1` on a Fluid row is the
   * signal that this particular vault is one of them.
   */
  observed?: {
    /** Distinct collateral assets this market actually accepts, this fetch. */
    collateralAssetCount: number
    /** Markets seen under this lender key on this chain, this fetch. */
    marketCount: number
    /** Does the lender key fan out to many markets (registry answer)? */
    multiMarketKey: boolean
  }
}

// ---------------------------------------------------------------------------
// Governance + oracle
// ---------------------------------------------------------------------------

export type AdminKind = Open<
  'EOA' | 'SAFE' | 'TIMELOCK' | 'GOVERNOR' | 'GOVERNANCE' | 'CUSTOM' | 'UNKNOWN'
>

export type GovernancePower = Open<
  | 'pause-deposits'
  | 'pause-withdrawals'
  | 'pause-borrows'
  | 'freeze-market'
  | 'change-ltv'
  | 'change-rate'
  | 'change-fees'
  | 'set-caps'
  | 'add-collateral'
  | 'swap-oracle'
  | 'upgrade-implementation'
  | 'blacklist'
  | 'seize-funds'
  | 'reprice-pending-redemptions'
  /** Move funds between already-approved markets — on curated vaults this is
   *  an allocator power and is NOT gated by the config timelock. */
  | 'reallocate'
>

export interface GovernanceTerms {
  mutability: Open<'immutable' | 'governed' | 'unknown'>
  /** The governance root, after hopping proxy admins / timelock admins. */
  controller?: string
  controllerKind?: AdminKind
  safe?: { threshold: number; owners: number }
  /**
   * Enforced delay in SECONDS between a parameter change being queued and it
   * taking effect — the holder's NOTICE PERIOD. `0`, or any `controllerKind`
   * that is not `TIMELOCK`, means a parameter can change in the very next
   * block with no warning.
   *
   * **This is NOT a withdrawal lock.** `SupplyExitTerms.cooldownSecs` is how
   * long YOUR money is stuck; this is how long you have to react to someone
   * else changing the deal. Never merge or sum the two.
   */
  timelockSecs?: number
  timelockSource?: Open<'on-chain' | 'screened' | 'metadata'>
  /**
   * The controller IS a timelock but its delay could not be read. Distinct
   * from `timelockSecs: undefined` on a non-timelock root, which genuinely
   * means "no notice period" — conflating the two would raise a false alarm
   * on the safest governance shape.
   */
  timelockUnknown?: boolean
  tier?: Open<'low' | 'medium' | 'high' | 'unknown'>
  score?: number
  powers?: GovernancePower[]
  roles?: {
    owner?: string
    curator?: string
    guardian?: string
    feeRecipient?: string
    /**
     * Addresses that can REALLOCATE a curated vault between its markets.
     *
     * Load-bearing next to `timelockSecs`, and the reason the two must be
     * read together: on the MetaMorpho shape a timelock gates adding a market
     * or raising a cap, but moving money BETWEEN already-approved markets is
     * an allocator call that lands in the next block. So a vault can publish
     * a 24-hour notice period and still change what a depositor is exposed to
     * with no notice at all. A sheet showing the timelock alone overstates
     * how much warning the holder gets.
     */
    allocators?: string[]
  }
  /** Governance screens refresh far slower than rates — own timestamp. */
  asOfScreen?: number
}

export type OracleBand = Open<'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL'>

export interface OracleTerms {
  /**
   * `none` is MEANINGFUL, not missing data: Teller liquidates on TIME and has
   * no oracle and no health factor anywhere in its trigger.
   */
  kind: Open<'price-feed' | 'nav-attested' | 'none'>
  /**
   * THE oracle for this `marketUid` — SINGULAR, lowercased. Verified across
   * the full oracle classification: 8,993 marketUids, 0 with more than one
   * address. Singularity holds because `marketUid` granularity is already
   * per-asset; the array in the legacy `oracleInfo.feeds[]` is an artifact of
   * hanging off the lender/params level instead.
   *
   * For a composite/cross adapter this is the ADAPTER — the address whose
   * failure or replacement moves this market's price.
   */
  address?: string
  /** Underlying feeds when the adapter composes several and the classifier
   *  decomposed them. `address` stays the single source of truth. */
  components?: string[]
  provider?: string
  /** Decoded reported pair, e.g. `"ETH / USD"`. */
  priceDescription?: string
  /** What it SHOULD report, e.g. `"WETH / USD"`. */
  intendedPair?: string
  correctAsset?: boolean | null
  correctNumeraire?: boolean | null
  fixedRate?: boolean
  score?: number
  band?: OracleBand
  flags?: string[]
  /** Can the oracle be swapped/upgraded, and by whom. On an otherwise
   *  IMMUTABLE market this is the ONLY mutable trust vector. */
  mutability?: {
    mutable: boolean
    kind: Open<'IMMUTABLE' | 'PROXY' | 'AUTHORITY' | 'UNKNOWN'>
    controller?: string
    controllerKind?: AdminKind
    timelockSecs?: number
  }
  heartbeatSecs?: number
  lastUpdateAt?: number
}

// ---------------------------------------------------------------------------
// Exposures
// ---------------------------------------------------------------------------

export interface AssetQuality {
  /** 1 (best) … 5 (worst). */
  riskScore?: number
  source?: Open<'whitelist' | 'default' | 'curated'>
  /** On-chain USD liquidity available to absorb a liquidation — the number
   *  that decides whether the LLTV is actually enforceable. */
  liquidityUsd?: number
  /** The TOKEN CONTRACT's own governance, distinct from the market's. An
   *  upgradeable, pausable collateral is a supplier risk even on an
   *  immutable market. */
  governanceScore?: number
  governanceLevel?: Open<'green' | 'amber' | 'red'>
  upgradeable?: boolean
  canPause?: boolean
  adminKind?: AdminKind
}

export interface ExposureEntry {
  asset: TermAssetRef
  /** That asset's OWN row in this lender — join key to its full term sheet. */
  marketUid?: string
  via: Open<'collateral' | 'vault-allocation' | 'strategy' | 'idle'>
  assets?: number
  assetsUsd?: number
  /** 0..100. ABSENT when `weightBasis === 'unweighted'`. */
  weightPct?: number
  ltv?: number
  liquidationLtv?: number
  liquidationPenalty?: number
  /** The collateral's OWN oracle. Your deposit's safety depends on the oracle
   *  pricing SOMEONE ELSE'S collateral. */
  oracle?: OracleTerms
  quality?: AssetQuality
}

export interface ExposureTerms {
  count: number
  /**
   * How `weightPct` was obtained, and therefore how much to trust it.
   * `unweighted` = POOLED lenders: Aave/Compound do not record which
   * collateral backs which borrow on-chain, so the list is the ACCEPTED SET,
   * not a measured split. Do not render a pie chart from it.
   */
  weightBasis: Open<'debt' | 'allocation' | 'unweighted'>
  worstRiskScore?: number
  worstOracleBand?: OracleBand
  /** Largest single exposure's `weightPct` — the concentration signal. Only
   *  meaningful when `weightBasis !== 'unweighted'`. */
  topWeightPct?: number
  items: ExposureEntry[]
}

// ---------------------------------------------------------------------------
// Utilization
// ---------------------------------------------------------------------------

export interface UtilizationTerms {
  /** borrowed / supplied, 0..1 — the IRM input for this market. */
  utilization: number
  /**
   * The basis the ratio is computed over. NOT always this row: rates for
   * shared-liquidity protocols are set on a larger pool, and a simulation
   * must shift THAT, not the row totals.
   */
  basis: Open<'market' | 'hub' | 'liquidity-layer' | 'pool'>
  irmTotalDeposits?: number
  irmTotalDebt?: number
  /** Where the curve steepens — headroom before the rate jumps. */
  targetUtilization?: number
  kinkUtilization?: number
  /** 0..1. `1` = cap full and the side is closed. */
  supplyCapUtilization?: number
  borrowCapUtilization?: number
  /** Fluid: share of collateral locked below the withdrawal limit. */
  lockupRatio?: number
}

// ---------------------------------------------------------------------------
// Modes
// ---------------------------------------------------------------------------

/**
 * A non-default risk category, expressed as a DELTA against the resolved
 * default. `config` is a MAP keyed by category (Aave e-modes, Dolomite
 * categories, Euler configs, Silo) — a single flat LTV silently reports the
 * default and hides the rest, which on an Aave ETH-correlated e-mode is the
 * difference between 80 % and 93 %.
 */
export interface ModeVariant {
  /** The `config` map key. `'0'` is the default on every lender. */
  modeId: string
  label?: string
  isDefault: boolean
  entry?: Open<'automatic' | 'user-selected' | 'per-position'>
  liquidation?: Partial<LiquidationTerms>
  /**
   * Mode-scoped and usually RESTRICTED: an e-mode typically narrows the
   * accepted collateral to a correlated basket. Omitting it would make the
   * headline LTV look obtainable against collateral the mode forbids.
   */
  acceptedCollateral?: ExposureTerms
  rate?: Partial<RateTerms>
  availability?: Partial<AvailabilityTerms>
}

// ---------------------------------------------------------------------------
// The two sides
// ---------------------------------------------------------------------------

export interface SupplyTermSheet {
  /** Is this position earning, or is it just collateral? */
  role: Open<'yield' | 'collateral' | 'both'>
  rate: RateTerms
  maturity: MaturityTerms
  exit: SupplyExitTerms
  /** ALL fees on this side, including the exit subset. */
  fees: FeeTerm[]
  /** What secures the debt drawn against this deposit. */
  backedBy?: ExposureTerms
  modes?: ModeVariant[]
  counterparty: CounterpartyTerms
  availability: AvailabilityTerms
  /** Is the supplied principal at risk beyond ordinary credit risk? */
  principal: {
    protected: boolean
    risks: Open<
      'bad-debt' | 'physical-delivery' | 'nav-drawdown' | 'first-loss' | 'depeg'
    >[]
  }
  info: TermInfo
  /** Namespaced escape hatch — see the promotion rule in TERM_SHEET_PLAN §13.5. */
  ext?: Record<string, unknown>
}

export interface BorrowTermSheet {
  rate: RateTerms
  maturity: MaturityTerms
  /**
   * Does the amount owed GROW, or is it a static face value fixed at trade
   * time? The single biggest departure from variable-rate intuition — four of
   * six fixed-term lenders are static.
   */
  debtShape: Open<'accruing' | 'static-face' | 'prepaid'>
  exit: BorrowExitTerms
  /** Fully resolved for the DEFAULT mode. `modes[]` carries the rest. */
  liquidation: LiquidationTerms
  /** What you may post, each with its own LTV, oracle and quality. */
  acceptedCollateral?: ExposureTerms
  modes?: ModeVariant[]
  fees: FeeTerm[]
  counterparty: CounterpartyTerms
  availability: AvailabilityTerms
  info: TermInfo
  ext?: Record<string, unknown>
}

// ---------------------------------------------------------------------------
// Coverage + envelope
// ---------------------------------------------------------------------------

/**
 * Distinguishes "not applicable" from "not implemented yet" — the affordance
 * that lets phases ship incrementally without lying. A missing `oracle` must
 * never read as "this market has no oracle" when the truth is "we have not
 * classified it".
 */
export interface CoverageInfo {
  /** Blocks genuinely computed for this market. */
  present: string[]
  /** Blocks that do NOT APPLY here — a positive fact. */
  notApplicable?: Record<string, string>
  /** Blocks that WOULD apply but are not wired yet. */
  pending?: Record<string, string>
}

/** Current schema version. Bumped ONLY for removals/semantic/unit changes;
 *  new optional fields and new enum members are additive. */
export const TERM_SHEET_SCHEMA_VERSION = 1

export interface TermSheet {
  schemaVersion: number
  /** unix seconds at fetch — everything here is a snapshot. */
  asOf: number
  /** `<family>.<variant>@v<n>`, e.g. `aave-v3.pool@v1`. Points at the prose
   *  catalogue so the per-market payload stays small. */
  profileId: string
  /** The anchor this sheet describes. */
  marketUid?: string
  lender?: string
  chainId?: string
  /**
   * The market's own underlying asset.
   *
   * Load-bearing, not decoration: `availability.minSize`, `availability.cap`
   * and `exit.minDebt` are all RAW base units, and without decimals + symbol a
   * consumer cannot render any of them. The sheet described a market without
   * ever saying which asset it was about.
   */
  asset?: TermAssetRef
  supply?: SupplyTermSheet
  borrow?: BorrowTermSheet
  /** Shared — these describe the MARKET, not a side. */
  governance?: GovernanceTerms
  oracle?: OracleTerms
  utilization?: UtilizationTerms
  constraints?: PositionConstraints
  coverage?: CoverageInfo
  ext?: Record<string, unknown>
}

/** Compact form for list endpoints — `?terms=digest`. */
export interface TermSheetDigest {
  schemaVersion: number
  profileId: string
  marketUid?: string
  supply?: {
    rateKind: RateKind
    aprTotal: AprPercent
    maturityKind: MaturityTerms['kind']
    maturity?: number
    exitMode: SupplyExitMode
    settlement: SupplyExitTerms['settlement']
    canOpen: boolean
    headline: string
    tags: TermTag[]
    backedBy?: Omit<ExposureTerms, 'items'>
  }
  borrow?: {
    rateKind: RateKind
    apr: AprPercent
    maturityKind: MaturityTerms['kind']
    maturity?: number
    debtShape: BorrowTermSheet['debtShape']
    earlyRepay: BorrowExitTerms['earlyRepay']
    liquidationTrigger: LiquidationTerms['trigger']
    canOpen: boolean
    headline: string
    tags: TermTag[]
    acceptedCollateral?: Omit<ExposureTerms, 'items'>
  }
  oracle?: Pick<OracleTerms, 'kind' | 'address' | 'provider' | 'band'>
  governance?: Pick<
    GovernanceTerms,
    'mutability' | 'controllerKind' | 'timelockSecs' | 'tier'
  >
  utilization?: number
}

/** A term profile — the invariant prose, one per lender family × variant. */
export interface TermProfile {
  id: string
  /** Display name, e.g. `Aave V3 pool market`. */
  name: string
  /** Which lender family this covers. */
  family: string
  supply?: { description: string; implications?: string[] }
  borrow?: { description: string; implications?: string[] }
  docsUrl?: string
}

/** Deep-partial, for adapters that return only what they override. */
export type DeepPartial<T> = {
  [K in keyof T]?: T[K] extends (infer U)[]
    ? U[]
    : T[K] extends object | undefined
      ? DeepPartial<NonNullable<T[K]>>
      : T[K]
}
